/**
 * What a landed hit does.
 *
 * The strike layer decides that a fist reached a body. This module decides
 * what the body does about it: both health layers take the blow, and the
 * fighter is pushed into a reaction. It holds no three.js, so the whole
 * consequence of a punch can be tested without a renderer.
 *
 * Strength earns its keep here rather than in the damage number. A `normal`
 * hit asks the state machine politely, so a fighter already committed to their
 * own uninterruptible strike eats it and keeps swinging — the trade, which is
 * the oldest decision in a fighting game. A `strong` hit ignores that and
 * takes the fighter's turn away from them.
 *
 * And when a blow empties the consciousness bar, the reaction is not a flinch.
 * A fighter who is out does not stagger and carry on; they go down.
 *
 * Knockback lives here too, because being shoved is a consequence of a blow in
 * exactly the way that taking damage is. Hitstop and camera shake do not: they
 * are how a hit *feels* rather than what it does, and they are in `impact.js`.
 */

import { clampToArena, MOVEMENT } from './locomotion.js';
import { climbOut, inPit, pitHit } from './pit.js';
import { isWeaponMove, reversalOf } from './weapons.js';
import { FEEL, feelKey } from './impact.js';
import { createGrapple } from './grapple.js';
import { contact, separate } from './surfaces.js';
import { matchesKO } from './styles.js';
import moveConfig from './moves.json';
import { blowFrom } from './strikeLayer.js';

/**
 * States a blow interrupts whatever the interrupt lock says.
 *
 * Two different reasons land in the same set.
 *
 * `stagger` and `blockStun` are uninterruptible so a fighter cannot *act* their
 * way out of a flinch — but a flinch is not a commitment, and the graph lists
 * `stagger -> stagger` precisely so a second blow can restart it. Without this,
 * a staggered fighter would be immune to reacting for the length of the
 * animation and every combo would land on a statue.
 *
 * `clinch` is the opposite: it *is* a commitment, and it is meant to lose to a
 * punch. That is the whole risk of reaching for a grapple, and it is why the
 * attempt has a startup window at all. It is uninterruptible in the graph only
 * so the fighter's own locomotion cannot cancel it on the very next frame —
 * being beaten to it by the other fighter is a different question, and this is
 * where it is answered.
 *
 * `thrown` is a reaction too, and the most helpless one in the game: a fighter
 * in the air chose nothing and can defend nothing. Without it here, arriving at
 * a wall would take the damage and leave them sailing on serenely.
 *
 * Only a state the fighter chose *and* is allowed to keep resists a normal hit.
 */
const YIELDS_TO_A_HIT = new Set(['stagger', 'blockStun', 'clinch', 'thrown']);

/**
 * Knockback.
 *
 * A blow pushes its target along the **attacker's facing**, not along the line
 * between the two bodies. Those are the same thing for a square exchange and
 * different the moment someone steps off the line, and the facing is the
 * honest one: a punch shoves you the way the fist was already going. Since
 * step 12 froze a striker's aim for the length of their strike, that direction
 * is also exactly where they committed.
 *
 * It is a short push rather than a teleport — the distance in `feel.json`
 * spread over `duration` at a constant speed, so the number in the config is
 * the metres actually travelled. Nothing else is writing to the position while
 * it runs: a knocked-back fighter is in `stagger` or `blockStun`, and
 * `locomotion` only moves a fighter who is standing, walking or guarding.
 *
 * The arena wall wins. A fighter shoved into it stops there instead of leaving
 * the floor, which is the beginning of corner pressure for free.
 */
function advancePushes(
  pushes,
  dt,
  { surfaces = [], radius = MOVEMENT.bodyRadius, onSurface, pit = null, onPit } = {}
) {
  // Walls, when there are any. `locomotion` keeps a fighter inside a circle
  // well short of them, so nobody can *walk* into one — but a fighter who has
  // been sent is out of that circle's jurisdiction, and clamping them to it
  // would stop them a metre and a half short of every wall in the arena.
  const walled = surfaces.some((surface) => surface.hard);
  const landed = [];
  const fell = [];

  for (const [target, push] of pushes) {
    const slice = Math.min(dt, push.remaining);
    const stepped = {
      x: target.position.x + push.x * slice,
      z: target.position.z + push.z * slice,
    };
    let to = walled ? stepped : clampToArena(stepped);

    // Before anything solid, because the floor stops before the wall does. A
    // fighter carried past the platform edge is falling, and what was on the
    // far side of the hole is no longer their problem.
    if (pit && inPit(to, pit)) {
      target.position.x = to.x;
      target.position.z = to.z;
      push.remaining -= slice;
      pushes.delete(target);
      fell.push([target, push, { ...to }]);
      continue;
    }

    // The wall gets the last word on where they end up, and the first word on
    // what it costs them.
    const struck = contact(to, radius, surfaces);
    if (struck) to = separate(to, struck);

    target.position.x = to.x;
    target.position.z = to.z;
    push.remaining -= slice;

    if (struck) {
      // A journey that ends against something does not also end on its own.
      pushes.delete(target);
      // Reported *after* the loop, never inside it. A wall hit is a hit, and a
      // hit starts a push — which would land back in this map while it is
      // still being walked, and in the worst case put the fighter straight
      // back into the wall it just came off. Once round the arena was enough
      // to find that one.
      landed.push([target, struck, push]);
      continue;
    }
    if (push.remaining <= 0) {
      pushes.delete(target);
      // A shove just stops. A throw has somewhere to be when it lands.
      push.onArrive?.(target);
    }
  }

  // Both reported after the loop and never inside it, for the same reason: a
  // wall hit is a hit, a hit starts a push, and a push landing back in this map
  // while it is still being walked is how a fighter once got put into the same
  // wall twice in one frame.
  for (const [target, struck, push] of landed) onSurface?.(target, struck, push);
  for (const [target, push, at] of fell) onPit?.(target, at, push);
}

/**
 * States in which the guard is up.
 *
 * `blockStun` is in here and that is the load-bearing part. A fighter holding
 * block who eats a blow is knocked into `blockStun`, and if that state did not
 * count as guarding then the *second* blow of any exchange would arrive on an
 * unguarded fighter — the guard would break itself the instant it was used.
 */
const GUARDING = new Set(['block', 'blockStun']);

/** States in which a fighter is committed to something and can be countered. */
const COUNTERABLE = new Set(['strike', 'taunt']);

/**
 * What a guard is worth.
 *
 * A blocked blow does no wellness damage at all, so it costs the defender
 * nothing permanent: wellness never recovers during a fight, and it is the
 * ceiling consciousness regenerates back to. What leaks through is a fraction
 * of the consciousness, through `drainConsciousness`, which is the model's own
 * door for damage the body does not absorb.
 *
 * That chip is small, but it resets the regen delay, so sustained pressure on a
 * guard still wears a fighter down — just far more slowly than landing clean.
 */
export const BLOCK = Object.freeze({ chip: 0.15 });

/**
 * Applies one hit to one fighter. Returns what each bar lost, whether the guard
 * took it, and which reaction the state machine accepted — `none` means the
 * machine refused, so the damage landed but the fighter fought through it.
 */
export function applyHit(hit, defender, { feel = FEEL } = {}) {
  const { health, machine } = defender;

  // A fighter who just missed is open. The multiplier is spent by this blow
  // whether it was guarded or not — the punish is the one shot, not the combo
  // that follows it.
  const exposure = defender.vulnerability ?? 1;
  const punished = exposure > 1;
  const incoming = punished ? { ...hit, damage: hit.damage * exposure } : hit;
  defender.consumeVulnerability?.();

  // Later hits in a chain are worth less, and **only in consciousness**. A
  // combo still wears a body down at full rate; it just stops being a way to
  // switch the lights off in one string.
  const chained = (incoming.consciousnessMultiplier ?? health.config.consciousnessMultiplier) *
    (incoming.comboScale ?? 1);

  // A counter is a blow that lands on a fighter who was doing something else —
  // mid-swing, or still open from having missed one. It is worth far more
  // momentum than a blow that lands on somebody just standing there, because
  // it took reading them to get it.
  const counter = COUNTERABLE.has(machine.state) || Boolean(defender.vulnerable);

  // Whether this blow is *allowed* to end the fight. Read before the damage
  // lands, because the danger flag has to have been earned already: the blow
  // that pushes someone into danger is not also the one that finishes them.
  const finisher = isFinisher(incoming, defender);
  /**
   * Every finisher in the game asks one question first: *was this fighter
   * already going down?* That is right for a punch and right for a wall — you
   * finish somebody who had it coming, and the survival floor is what stops a
   * clean fighter being deleted by one good shot.
   *
   * `lethal` is the one flag that does not ask. Nothing wears it but a moving
   * train, and a train is not a technique that needs setting up. A fighter left
   * standing at one point of consciousness after being hit by one would be a
   * worse lie than the floor is a protection.
   */
  const canFinish = incoming.lethal || (finisher && health.inDanger);
  const floor = canFinish ? 0 : health.config.survivalFloor;

  // A guard is worth nothing against a pipe. `unblockable` is carried on the
  // blow rather than asked of the attacker, so the arena and the crowd could
  // use it too without any of them learning what a weapon is.
  const blocked = GUARDING.has(machine.state) && !incoming.unblockable;
  const damage = blocked
    ? absorb({ ...incoming, consciousnessMultiplier: chained }, health, floor)
    : health.applyDamage(incoming.damage, {
        consciousnessMultiplier: chained,
        floor,
        wellnessScale: incoming.wellnessScale ?? 1,
        part: incoming.limbTarget ?? incoming.part,
        limbScale: incoming.limbScale ?? 1,
      });

  const ignoreInterrupt = hit.strength === 'strong' || YIELDS_TO_A_HIT.has(machine.state);
  const flinch = blocked ? 'blockStun' : 'stagger';

  const knockout = health.isOut;
  let reaction = 'none';
  if (knockout) {
    // Terminal. Nothing refuses it and nothing follows it.
    if (machine.request('dead', { ignoreInterrupt: true })) reaction = 'dead';
  } else if (damage.floored) {
    // Emptied, but by a blow that was never going to finish it. They go down
    // and they get back up — which is what `knockdown -> getup` was always for.
    if (machine.request('knockdown', { ignoreInterrupt: true })) reaction = 'knockdown';
  } else if (machine.request(flinch, { ignoreInterrupt })) {
    reaction = flinch;
  }

  return {
    ...damage,
    blocked,
    punished,
    counter,
    finisher,
    knockout,
    reaction,
    knockback: knockbackFor(incoming, blocked, feel),
  };
}

/**
 * Whether a blow is the kind that is allowed to end a fight.
 *
 * Only three kinds are: a **heavy** strike, a blow thrown while **blazin**, and
 * one flagged **environmental** — a fighter put through something rather than
 * hit by someone. Everything else can take a fighter to the edge and leave them
 * there, which is the whole shape of the two-layer health model finally being
 * cashed in: wellness decides *how close to the edge* a fighter is, and only a
 * moment worth watching is allowed to push them over it.
 */
export function isFinisher(hit, defender = null) {
  // Two things finish a fight whatever anybody's style is. A blazin move,
  // because that is what the meter was spent on; and the arena, because a
  // fighter put through a wall is not being beaten by a technique.
  if (hit.blazin || hit.environmental) return true;
  // Otherwise the attacker's primary style decides. Without one — which is
  // every stub defender in half these tests — it is the old global rule, which
  // is also exactly what street fighting declares.
  if (hit.koCondition) return matchesKO(hit.koCondition, hit, defender);
  return hit.strength === 'strong';
}

/**
 * How far a blow shoves.
 *
 * A move may name its own distance, and the strength table in `feel.json` is
 * the fallback for anything that does not. That override is what makes combos
 * possible at all: a jab that pushed its target the full `normal` distance
 * would shove them out of the reach of the follow-up it just opened the window
 * for, and every chain would whiff on its second hit. Links in a chain barely
 * move anybody; the ender is what sends them away.
 *
 * A guard is still the guard's business, and takes the blocked figure whatever
 * the move wanted.
 */
function knockbackFor(hit, blocked, feel) {
  if (blocked) return feel.knockback.blocked ?? 0;
  return hit.knockback ?? feel.knockback[feelKey(hit)] ?? 0;
}

/** A blocked blow: no wellness, and a fraction of the consciousness. */
function absorb(hit, health, floor = 0) {
  const multiplier = hit.consciousnessMultiplier ?? health.config.consciousnessMultiplier;
  const wanted = hit.damage * multiplier * BLOCK.chip;
  const consciousness = health.drainConsciousness(wanted, { floor });
  // The floor stopped it if the drain took less than it asked for and came to
  // rest exactly on it — a guard broken by chip, rather than worn down by it.
  const floored = floor > 0 && consciousness < wanted - 1e-9 && health.consciousness === floor;
  return { wellness: 0, consciousness, floored };
}

/**
 * Holds the combatants and runs a frame of the fight.
 *
 * The two passes are not decoration. Every fighter is posed for this frame
 * first, and only then is any strike resolved — otherwise whoever happened to
 * be updated first would be punching at where their opponent stood last frame.
 */
export function createFight({
  onHit,
  onKnockout,
  feel = FEEL,
  frameRate = moveConfig.frameRate,
  /** The arena's hard faces. Empty means nothing to be put through. */
  surfaces: initialSurfaces = [],
  /** The arena's hole, if it has one. Null means the floor goes all the way. */
  pit: initialPit = null,
  /** The walkable floor, for putting somebody back on it after a fall. */
  bounds: initialBounds = null,
  /**
   * Whether the pit is currently the kind you do not get out of.
   *
   * A predicate rather than a flag, and deliberately not a train: the pit has
   * no business knowing about timetables, and the train has no business knowing
   * about damage. An arena could make its pit lethal for any reason it liked
   * and neither of them would change.
   */
  pitLethal: initialPitLethal = () => false,
} = {}) {
  let pitLethal = initialPitLethal;
  let pit = initialPit;
  let bounds = initialBounds;
  /**
   * Not a const any more, for two reasons that arrived together: an arena can
   * be swapped at runtime, and a train is a hard surface that is only there
   * some of the time. Read fresh every frame rather than captured.
   */
  let surfaces = [...initialSurfaces];
  const combatants = [];
  const listeners = new Set();
  const knockoutListeners = new Set();
  const surfaceListeners = new Set();
  const pitListeners = new Set();
  const reversalListeners = new Set();
  if (onHit) listeners.add(onHit);
  if (onKnockout) knockoutListeners.add(onKnockout);
  /** Fighters currently travelling backwards. Cleared as each push runs out. */
  const pushes = new Map();

  /**
   * The clinch. At most one at a time, held here rather than on either fighter
   * because it belongs to neither of them — it is the thing between them.
   */
  const grapple = createGrapple({ frameRate });

  grapple.onResolve(({ holder, held, move, name, outcome, direction }) => {
    if (outcome === 'completed') {
      // The hold ran its course: the damage lands now, all of it at once, on a
      // fighter who has had no way to defend for the length of it.
      /**
       * The second place a blow was assembled from a hand-picked list, and it
       * had drifted from the first: this one remembered `environmental` — which
       * is the whole of the wall slam — and the strike layer's did not.
       *
       * Both go through `blowFrom` now. A hold that runs its course lands the
       * move's own flags, so a grapple that is ever made unblockable, or given
       * a weapon, needs nothing here.
       */
      land(
        holder,
        held,
        blowFrom(move, {
          // The **key**, not the display name. `strike` is read as a key by
          // everything downstream — `combo.landed` puts it straight into the
          // chain — and a hold that landed "Wall Slam" there crashed the next
          // follow-up that went looking for its routes.
          strike: name ?? move.name,
          kind: 'grapple',
          damage: move.damage,
          // A fighter who has had no way to defend for the length of a hold
          // takes it harder than the same damage thrown at them standing up.
          consciousnessMultiplier: 1.6,
          ender: true,
          target: held.id,
          part: 'clinch',
        })
      );
      holder.machine.request('idle', { ignoreInterrupt: true });
    } else if (outcome === 'whipped') {
      // Sent, not released. `thrown` is not a locomotion state, so nothing
      // steers them once they are in the air — and it does not loop into
      // anything, so the travel is the only thing that can end it.
      held.machine.request('thrown', { ignoreInterrupt: true });
      holder.machine.request('idle', { ignoreInterrupt: true });
      const whip = move.whip ?? {};
      shove(held, direction, whip.distance, {
        duration: whip.duration,
        by: holder,
        // Unless something stops them first. As of step 22, something can.
        onArrive: (fighter) => fighter.machine.request('knockdown', { ignoreInterrupt: true }),
      });
    } else if (outcome === 'escaped') {
      // Breaking out is not neutral. The holder eats the recovery they would
      // have spent on the throw, which is what makes the window worth hitting.
      holder.machine.request('stagger', { ignoreInterrupt: true });
      held.machine.request('idle', { ignoreInterrupt: true });
    }
    // `broken` needs nothing: whatever pulled them out has already set both
    // states, which is how it got noticed in the first place.
  });

  function find(id) {
    return combatants.find((c) => c.id === id) ?? null;
  }

  /**
   * One blow arriving on one fighter, with everything that hangs off it.
   *
   * Split out of the strike listener because a grapple's damage arrives here
   * too, at the end of the hold rather than on contact — and it has to be
   * priced, paid for and announced in exactly the same way.
   */
  function land(attacker, defender, hit) {
    // Read the chain's worth *before* pushing this blow onto it: the scale
    // belongs to the hit that is landing, not to the one after.
    const chain = attacker.combo;
    const enriched = {
      ...hit,
      comboScale: chain?.scale ?? 1,
      chainIndex: chain?.length ?? 0,
      // Whether the attacker was blazin when it connected. Read by the KO
      // rules, which are choosy about what is allowed to finish.
      blazin: Boolean(attacker.blazin),
      // And how their style says a fight ends. Carried on the blow rather than
      // looked up, so `applyHit` stays a function of the hit and the defender
      // and nothing else.
      koCondition: attacker.loadout?.ko ?? null,
      // A style's damage multiplier scales the blow; its wellness ratio decides
      // how much of that sticks to the body rather than only being felt. A
      // submission fighter hits softly and permanently barely at all.
      damage: hit.damage * (attacker.loadout?.stats.damage ?? 1),
      wellnessScale: attacker.loadout?.stats.wellnessRatio ?? 1,
    };

    // Whether the attacker was in danger *before* this blow resolved. A
    // comeback is won from the floor, and by the time the knockout is reported
    // the defender's health has changed but the attacker's has not — so this is
    // read here only for symmetry with where it is used.
    const attackerInDanger = Boolean(attacker.health?.inDanger);

    /**
     * What is about to change hands, if this turns out to be a counter.
     *
     * Read **before** the blow resolves, and that is not fussiness: putting a
     * fighter into `stagger` cancels whatever they were swinging, so by the time
     * `applyHit` returns there is no swing left to have caught them in the
     * middle of. The counter itself is `applyHit`'s answer, so the two halves of
     * the question are asked either side of it.
     */
    const atRisk = reversalOf(defender, { counter: true });

    const result = applyHit(enriched, defender, { feel });
    chain?.landed(hit.strike);
    // Landing pays, countering pays much better, and a blow eaten by a guard
    // pays almost nothing — you got their attention, not their jaw.
    attacker.momentum?.gain(
      result.blocked ? 'blocked' : result.counter ? 'counter' : 'hit',
      attacker.loadout?.stats.momentum ?? 1
    );

    // And the other meter. Heat does not care that a blow landed — landing is
    // what fighters do — only that it was one of the few kinds worth watching.
    if (!result.blocked) {
      if (result.counter) attacker.heat?.record('counter', { opponent: defender.id });
      if (enriched.blazin) attacker.heat?.record('blazin', { opponent: defender.id });
      // The source that has been in the table since step 35 with nothing to
      // fire it. A weapon landing is exactly what it was reserved for.
      if (isWeaponMove(enriched)) attacker.heat?.record('weapon', { opponent: defender.id });
    }

    /**
     * The reversal: a counter landed on somebody mid-swing takes the weapon.
     *
     * The **same object** moves across, with the swings it has left on it —
     * taking a half-broken bottle off somebody is not the same as being handed
     * a fresh one, and it is the difference between a reversal and a reward.
     *
     * `arm` cancels whatever the attacker was doing, which is a supported path:
     * the strike layer has tolerated a listener ending the move from inside
     * `resolve` since the clinch needed it in step 20.
     */
    if (atRisk && result.counter) {
      defender.disarm?.();
      attacker.arm?.(atRisk);
      for (const listener of reversalListeners) {
        listener({ weapon: atRisk, id: atRisk.id, from: defender.id, to: attacker.id });
      }
    }
    // Being hit ends your own chain. You were not the one comboing.
    defender.combo?.reset();

    shove(defender, attacker.facing, result.knockback, { by: attacker });
    const event = { ...enriched, attacker: attacker.id, ...result };
    for (const listener of listeners) listener(event);
    if (result.knockout) {
      /**
       * How a fight ends is the biggest single thing heat reads, and the three
       * cases are three different stories.
       *
       * A finish off the arena is the loudest thing in the game. A win taken
       * while the winner was themselves on the edge is a comeback. Anything
       * else is a fighter doing their job, and doing your job is not a story —
       * it is the only fall in the table that is not a fighter being dull on
       * purpose, and it is there so that *how* you win matters as much as
       * whether you do.
       *
       * The first two are not exclusive: a comeback finished against a wall is
       * both, and gets both.
       */
      /**
       * A weapon kill counts as a finish on the arena.
       *
       * Not because a bat is a wall, but because heat is asking the same
       * question of both — *was this a thing worth telling somebody about* —
       * and "he finished him with a pipe" and "he put him through a wall" are
       * the same answer. It is also the reason a weapon kill is not a
       * `plainWin`: winning with a bat is not doing your job.
       */
      const environmental = Boolean(enriched.environmental) || isWeaponMove(enriched);
      if (environmental) {
        attacker.heat?.record('environmentalFinish', { opponent: defender.id });
      }
      if (attackerInDanger) {
        attacker.heat?.record('dangerWin', { opponent: defender.id });
      }
      if (!environmental && !attackerInDanger) {
        attacker.heat?.record('plainWin', { opponent: defender.id });
      }

      for (const listener of knockoutListeners) {
        listener({ winner: attacker.id, loser: defender.id, hit: event });
      }
    }
    return result;
  }

  /**
   * **Out is out, however they got there.**
   *
   * The knockout used to be a property of a *blow*: `applyHit` asked
   * `health.isOut` the instant one landed, and nothing asked at any other time.
   * That is one moment short of the truth, because the wellness bar is the
   * ceiling on consciousness and wellness only ever falls. A fighter emptied by
   * a blow the survival floor stopped at one point of consciousness goes down,
   * gets up, and then loses that last point *between* blows, when `health.update`
   * pulls consciousness down to a ceiling that is now zero.
   *
   * They then stand there awake at zero, and the round never ends — which is
   * exactly what eight of twenty-four headless AI fights did before this
   * existed. The dummy hid it: it kept swinging, and the next blow to land
   * asked the question again. Two fighters with the sense to stop hitting
   * somebody who is finished do not.
   */
  function outCold() {
    for (const combatant of combatants) {
      if (!combatant.health.isOut) continue;
      // Already announced. The state is the record that it happened, and it is
      // terminal — nothing transitions out of `dead`.
      if (combatant.machine.state === 'dead') continue;
      combatant.machine.request('dead', { ignoreInterrupt: true });

      const winner = combatants.find((other) => other !== combatant) ?? null;
      // A win is a win, and the meter should see one. There is no blow to ask
      // about here, so it is the plain kind by definition: nothing about
      // running somebody's body down is environmental or done with a weapon.
      winner?.heat?.record('plainWin', { opponent: combatant.id });

      for (const listener of knockoutListeners) {
        // No `hit`: nothing landed this frame. Whoever is listening has to
        // cope with a finish that has no blow behind it, because there is one.
        listener({ winner: winner?.id ?? null, loser: combatant.id, hit: null });
      }
    }
  }

  function shove(
    target,
    direction,
    distance,
    { duration = feel.knockback.duration, onArrive, by = null } = {}
  ) {
    if (!(distance > 0) || !(duration > 0) || !direction) return false;
    pushes.set(target, {
      x: direction.x * (distance / duration),
      z: direction.z * (distance / duration),
      remaining: duration,
      onArrive,
      // Who sent them. A wall does not throw anybody into itself.
      by,
    });
    return true;
  }

  /**
   * A fighter arriving at something solid, fast.
   *
   * This is the third kind of blow the game has — not a strike, not a grapple,
   * but the arena itself — and it is the reason `isFinisher` has always had an
   * `environmental` clause with nothing to fill it. A fighter in danger can be
   * finished by being put through a wall, and by almost nothing else that is
   * not a heavy or a blazin move.
   *
   * It is credited to whoever sent them. The wall did not decide to be there.
   */
  function strikeSurface(fighter, struck, push) {
    const { surface } = struck;
    const attacker = push?.by ?? fighter;
    const result = land(attacker, fighter, {
      strike: surface.id,
      kind: 'environmental',
      environmental: true,
      strength: 'normal',
      damage: surface.damage,
      consciousnessMultiplier: 2.2,
      // Not along the attacker's facing, which points *at* the wall. A fighter
      // who hits something comes off it, and that has to be its own shove.
      knockback: 0,
      target: fighter.id,
      part: surface.id,
      surface: surface.id,
    });
    // The rebound: along the direction out of what they hit, so it can only
    // ever carry them back into the arena. Taken from the contact rather than
    // from the surface, because a column's "out" depends on which side of it
    // they arrived at — and a wall's is the same number either way.
    shove(fighter, struck.normal ?? surface.normal, surface.knockback ?? 0, { by: attacker });
    // And the window the whole of step 23 hangs off: a fighter peeled off a
    // wall is reeling, and reeling is the only state a finisher is available
    // against. How long is the surface's business, not the fighter's.
    if (!result.knockout && surface.stun > 0) fighter.stun?.(surface.stun / frameRate);
    // Where on it, and from which side. An arena that shows the damage — a
    // dented bonnet, a broken bar — needs to know which part of the thing took
    // the body, and a column's or a box's side is only known from the contact.
    const at = { x: fighter.position.x, z: fighter.position.z };
    const normal = struck.normal ?? surface.normal;
    for (const listener of surfaceListeners) {
      listener({ fighter: fighter.id, surface: surface.id, by: attacker.id, at, normal, ...result });
    }
    return result;
  }

  /**
   * A fighter going over the edge.
   *
   * The pit's sibling to `strikeSurface`, and the difference between them is
   * the whole point of it being a region rather than a face: there is no
   * rebound, because there is nothing to rebound off, and where they end up
   * afterwards depends on whether they are getting up at all.
   */
  function fallIntoPit(fighter, at, push) {
    // Asked *where*: a pit is only lethal at the place the danger is (step 97 —
    // a train in the tunnel is in the station's timetable, not on the track).
    const lethal = pitLethal(at);
    const attacker = push?.by ?? fighter;
    const result = land(attacker, fighter, {
      ...pitHit(pit, { lethal }),
      target: fighter.id,
    });

    if (!result.knockout) {
      // They climb out. Not a flinch — a fighter who has been in the track gets
      // back onto the platform on their hands and knees, which is what
      // `knockdown -> getup` has been for since step 4.
      const spot = climbOut(at, pit, bounds);
      fighter.position.x = spot.x;
      fighter.position.z = spot.z;
      fighter.machine?.request?.('knockdown', { ignoreInterrupt: true });
      if (pit.stun > 0) fighter.stun?.(pit.stun / frameRate);
    }

    const event = {
      fighter: fighter.id,
      surface: pit?.id ?? 'pit',
      by: attacker.id,
      // Where they went in, not where they climbed out — the place over the
      // edge a picture of the fall starts from.
      at: { x: at.x, z: at.z },
      pit: true,
      lethal,
      ...result,
    };
    for (const listener of surfaceListeners) listener(event);
    for (const listener of pitListeners) listener(event);
    return result;
  }

  return {
    get combatants() {
      return [...combatants];
    },

    add(...added) {
      for (const combatant of added) {
        combatants.push(combatant);
        combatant.strikes.onHit((hit) => {
          const defender = find(hit.target);
          if (!defender) return;

          // A clinch that connects does not do damage. It starts a hold, and
          // the damage — if any — comes at the end of it.
          if (hit.kind === 'grapple') {
            const move = combatant.strikes.get(hit.strike);
            if (move && grapple.begin(combatant, defender, move, hit.strike)) {
              defender.combo?.reset();
              for (const listener of listeners) {
                // Same shape as any other hit event, with the damage fields at
                // zero — because nothing has been taken yet. Anyone watching
                // hits should not have to special-case a clinch to read one.
                listener({
                  ...hit,
                  attacker: combatant.id,
                  wellness: 0,
                  consciousness: 0,
                  knockback: 0,
                  blocked: false,
                  punished: false,
                  counter: false,
                  finisher: false,
                  knockout: false,
                  reaction: 'grappled',
                  grappled: true,
                });
              }
            }
            return;
          }

          land(combatant, defender, hit);
        });
      }
      return this;
    },

    /** The clinch, for a control layer that needs to offer an escape. */
    grapple,

    find,

    update(dt) {
      // Pass zero: anyone still travelling from an earlier blow arrives before
      // they are posed, so their boxes are built on where they now are.
      advancePushes(pushes, dt, {
        surfaces,
        pit,
        onSurface: strikeSurface,
        onPit: fallIntoPit,
      });

      // And then the clinch, which overrides all of it: a held fighter's
      // position is not theirs, so a shove must not be able to drag them off
      // the mark the hold puts them on.
      grapple.update(dt);

      // Pass one: pose everyone, tick their health, move their boxes.
      for (const combatant of combatants) combatant.update(dt);

      // Pass two: now that the world agrees on where every limb is, see what
      // the live strikes touched.
      for (const combatant of combatants) {
        if (!combatant.strikes.active) continue;
        combatant.strikes.update(dt, {
          hitboxes: combatant.hitboxes,
          targets: combatants.filter((o) => o !== combatant).map((o) => o.asTarget()),
        });
      }

      // Pass three: is anybody out who nobody noticed?
      outCold();
    },

    onHit(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },

    /** Fires once, on the blow that actually finishes somebody. */
    onKnockout(listener) {
      knockoutListeners.add(listener);
      return () => knockoutListeners.delete(listener);
    },

    /** Fires when a fighter arrives at a hard surface, fast. */
    onSurface(listener) {
      surfaceListeners.add(listener);
      return () => surfaceListeners.delete(listener);
    },

    /** Fires when a weapon changes hands, with who lost it and who has it. */
    onReversal(listener) {
      reversalListeners.add(listener);
      return () => reversalListeners.delete(listener);
    },

    /** Fires when a fighter goes over the edge. Also reported to `onSurface`. */
    onPit(listener) {
      pitListeners.add(listener);
      return () => pitListeners.delete(listener);
    },

    /** The arena's hole, as the rules see it. */
    get pit() {
      return pit;
    },

    /**
     * Swaps the arena's hole, its walkable floor and the question of whether
     * the hole is currently the kind you do not get out of.
     *
     * All three at once and on purpose: they are one arena's worth of facts,
     * and a pit from one arena tested against another one's floor would put a
     * fighter back onto a platform that is not there.
     */
    setPit(next = null, { bounds: nextBounds = null, lethal } = {}) {
      pit = next;
      bounds = nextBounds;
      pitLethal = lethal ?? (() => false);
      return pit;
    },

    /** The arena's hard faces, as the collision sees them. */
    get surfaces() {
      return surfaces;
    },

    /** Swaps the whole arena out. What step 34's train and step 36's loader use. */
    setSurfaces(next = []) {
      surfaces = [...next];
      return surfaces;
    },

    /**
     * Puts one surface in or takes it out, leaving the rest alone.
     *
     * A moving surface is held **by reference** — the train rewrites its own
     * footprint every frame and the collision reads it where it lies, so this
     * must never copy the object it is handed.
     */
    addSurface(surface) {
      if (surface && !surfaces.includes(surface)) surfaces.push(surface);
      return surfaces;
    },
    removeSurface(surface) {
      const at = surfaces.indexOf(surface);
      if (at >= 0) surfaces.splice(at, 1);
      return surfaces;
    },

    /**
     * Puts a fighter in motion, with nothing having hit them.
     *
     * The explicit debug door, the same way `machine.force()` is: everything in
     * the fight that sends somebody travelling goes through the same private
     * `shove`, and an arena is a great deal easier to check if you can throw a
     * body at a named wall without first setting up a grapple to do it with.
     * Combat code should never call this — it would be a push with no blow
     * behind it, credited to nobody.
     */
    push(target, direction, distance, options) {
      return shove(target, direction, distance, options);
    },

    /** Fighters currently being knocked back. For the debug overlay. */
    get travelling() {
      return [...pushes.keys()].map((c) => c.id);
    },

    dispose() {
      for (const combatant of combatants) combatant.dispose?.();
      combatants.length = 0;
      listeners.clear();
      knockoutListeners.clear();
      surfaceListeners.clear();
      pitListeners.clear();
      reversalListeners.clear();
      pushes.clear();
      grapple.breakOff();
    },
  };
}
